import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  MagnifyingGlassIcon,
  VideoCameraIcon,
  FilmIcon,
  ScissorsIcon,
  SparklesIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { videoApi, VideoUploadResponse, VideoTranslationJob, YouTubeUploadEntry } from '@/services/videoApi';
import { youtubeApi } from '@/services/youtubeApi';
import { usePermissions } from '@/hooks/usePermissions';
import UploadVideoModal from '@/components/Video/UploadVideoModal';
import YouTubePublishModal from '@/components/Video/YouTubePublishModal';
import SocialPublishModal from '@/components/Video/SocialPublishModal';

interface QuickVideo {
  type: 'upload' | 'translation';
  id: string;
  title: string;
  thumbnailUrl?: string;
  createdAt: string;
  existingUploads: YouTubeUploadEntry[];
}

const QuickAction: React.FC<{
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  color: string;
  onClick: () => void;
}> = ({ icon: Icon, label, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition-all"
  >
    <div className={`h-10 w-10 rounded-lg ${color} flex items-center justify-center`}>
      <Icon className="h-5 w-5 text-white" />
    </div>
    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
  </button>
);

const VideoQuickStart: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<QuickVideo | null>(null);
  const [publishYouTubeOpen, setPublishYouTubeOpen] = useState(false);
  const [publishMetaOpen, setPublishMetaOpen] = useState(false);

  const { data: uploadsData, isLoading: uploadsLoading } = useQuery({
    queryKey: ['video-uploads'],
    queryFn: () => videoApi.getUploads(),
    enabled: searchOpen,
  });

  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['video-jobs'],
    queryFn: () => videoApi.getJobs({ limit: 100 }),
    enabled: searchOpen,
  });

  const { data: configData } = useQuery({
    queryKey: ['youtube-config'],
    queryFn: () => youtubeApi.getConfig(),
  });
  const youtubeEnabled = configData?.enabled && configData?.globallyConfigured;

  const quickVideos: QuickVideo[] = useMemo(() => {
    const uploads: QuickVideo[] = (uploadsData?.uploads || []).map((u) => ({
      type: 'upload',
      id: u.fileId,
      title: u.originalName,
      thumbnailUrl: u.thumbnailUrl,
      createdAt: u.uploadedAt,
      existingUploads: u.youtubeUploads || [],
    }));
    const jobs: QuickVideo[] = ((jobsData?.jobs || []) as VideoTranslationJob[])
      .filter((j) => j.status === 'completed' && j.result?.finalVideoUrl)
      .map((j) => ({
        type: 'translation',
        id: j.id,
        title: j.title || `Translation ${j.id}`,
        thumbnailUrl: j.thumbnailUrl,
        createdAt: j.createdAt,
        existingUploads: j.youtubeUploads || [],
      }));
    return [...uploads, ...jobs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [uploadsData, jobsData]);

  const filteredVideos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const matches = term ? quickVideos.filter((v) => v.title.toLowerCase().includes(term)) : quickVideos;
    return matches.slice(0, 8);
  }, [quickVideos, searchTerm]);

  const handleSelectVideo = (video: QuickVideo) => {
    setSelected(video);
    setSearchOpen(false);
    setSearchTerm('');
  };

  const handleUploaded = (result: VideoUploadResponse) => {
    setUploadModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['video-uploads'] });
    setSelected({
      type: 'upload',
      id: result.fileId,
      title: result.originalName,
      createdAt: new Date().toISOString(),
      existingUploads: [],
    });
  };

  const handleTranslate = () => {
    if (!selected || selected.type !== 'upload') return;
    navigate('/app/audio/video-translation', {
      state: { fileId: selected.id, fileName: selected.title },
    });
  };

  const getShareableUrl = (video: QuickVideo) =>
    video.type === 'upload' ? videoApi.getUploadShareableUrl(video.id) : videoApi.getJobShareableUrl(video.id);

  const handleGenerateClips = async () => {
    if (!selected) return;
    try {
      const { url } = await getShareableUrl(selected);
      const params = new URLSearchParams({ videoS3Url: url, title: selected.title });
      if (selected.type === 'translation') params.set('jobId', selected.id);
      navigate(`/app/clips?${params.toString()}`);
    } catch {
      toast.error('Failed to get video URL for clip generation');
    }
  };

  const handleUnderstand = async () => {
    if (!selected) return;
    try {
      const { url } = await getShareableUrl(selected);
      const params = new URLSearchParams({ videoS3Url: url, title: selected.title });
      navigate(`/app/video-understanding?${params.toString()}`);
    } catch {
      toast.error('Failed to get video URL for video understanding');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-6 sm:p-8">
        {!selected ? (
          <>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Start with a video</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Upload a new video or pick one you already have — then translate it, generate clips, get an AI
              breakdown, or publish it straight to YouTube or Meta.
            </p>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setUploadModalOpen(true)}
                className="group flex items-center gap-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 p-5 text-left transition-colors"
              >
                <div className="h-12 w-12 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
                  <CloudArrowUpIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Upload a new video</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">MP4, MOV, WebM · up to 500MB</p>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                className="group flex items-center gap-4 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 p-5 text-left transition-colors"
              >
                <div className="h-12 w-12 rounded-lg bg-gray-700 dark:bg-gray-600 flex items-center justify-center shrink-0">
                  <MagnifyingGlassIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Choose an existing video</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Search your uploads &amp; translations</p>
                </div>
              </button>
            </div>

            {searchOpen && (
              <div className="mt-5">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by title..."
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
                <div className="mt-3 max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                  {uploadsLoading || jobsLoading ? (
                    <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">Loading your videos…</p>
                  ) : filteredVideos.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">No videos found.</p>
                  ) : (
                    filteredVideos.map((v) => (
                      <button
                        key={`${v.type}-${v.id}`}
                        type="button"
                        onClick={() => handleSelectVideo(v)}
                        className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="h-10 w-16 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0 flex items-center justify-center">
                          {v.thumbnailUrl ? (
                            <img src={v.thumbnailUrl} alt={v.title} className="h-full w-full object-cover" />
                          ) : v.type === 'upload' ? (
                            <VideoCameraIcon className="h-5 w-5 text-gray-300 dark:text-gray-600" />
                          ) : (
                            <FilmIcon className="h-5 w-5 text-gray-300 dark:text-gray-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{v.title}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                            {v.type === 'upload' ? 'Uploaded' : 'Translated'} · {new Date(v.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <div className="h-16 w-28 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0 flex items-center justify-center">
                {selected.thumbnailUrl ? (
                  <img src={selected.thumbnailUrl} alt={selected.title} className="h-full w-full object-cover" />
                ) : (
                  <FilmIcon className="h-6 w-6 text-gray-300 dark:text-gray-600" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">
                  {selected.type === 'upload' ? 'Uploaded video' : 'Translated video'}
                </p>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">{selected.title}</h2>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="mt-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:underline"
                >
                  Choose a different video
                </button>
              </div>
            </div>

            <p className="mt-6 text-sm font-medium text-gray-700 dark:text-gray-300">What would you like to do?</p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {selected.type === 'upload' && hasPermission('video-translation', 'create') && (
                <QuickAction icon={PlayIcon} label="Translate" color="bg-indigo-600" onClick={handleTranslate} />
              )}
              {hasPermission('clip-generation', 'create') && (
                <QuickAction icon={ScissorsIcon} label="Generate Clips" color="bg-indigo-600" onClick={handleGenerateClips} />
              )}
              {hasPermission('video-understanding', 'create') && (
                <QuickAction icon={SparklesIcon} label="Understand" color="bg-purple-600" onClick={handleUnderstand} />
              )}
              {youtubeEnabled && (
                <QuickAction
                  icon={VideoCameraIcon}
                  label="Publish to YouTube"
                  color="bg-red-600"
                  onClick={() => setPublishYouTubeOpen(true)}
                />
              )}
              {selected.type === 'upload' && (
                <QuickAction
                  icon={CloudArrowUpIcon}
                  label="Publish to Meta"
                  color="bg-blue-600"
                  onClick={() => setPublishMetaOpen(true)}
                />
              )}
            </div>
          </>
        )}
      </div>

      {uploadModalOpen && (
        <UploadVideoModal onClose={() => setUploadModalOpen(false)} onUploaded={handleUploaded} />
      )}

      {publishYouTubeOpen && selected && (
        <YouTubePublishModal
          sourceType={selected.type}
          sourceId={selected.id}
          defaultTitle={selected.title}
          existingUploads={selected.existingUploads}
          onClose={() => setPublishYouTubeOpen(false)}
          onPublished={() => {
            setPublishYouTubeOpen(false);
            toast.success('Published to YouTube!');
          }}
        />
      )}

      {publishMetaOpen && selected && selected.type === 'upload' && (
        <SocialPublishModal
          sourceType="upload"
          sourceId={selected.id}
          defaultCaption={selected.title}
          onClose={() => setPublishMetaOpen(false)}
          onPublished={() => {
            setPublishMetaOpen(false);
            toast.success('Published!');
          }}
        />
      )}
    </div>
  );
};

export default VideoQuickStart;
